import { ComponentFactory } from '../factories/ComponentFactory';
import { FeederFactory } from '../factories/FeederFactory';
import { generateUniqueId } from './generateUniqueId';
import { BoxCommand, MainPanelCommand, PanelCommand, SocketCommand } from '../modules/undoRedo';

/**
 * Boş sahnede gerçek tesisat komponentlerinden bir demo şema kurar:
 * Supply → MainPanel (ADP) → Panel (ZKT) zinciri. Komponentler ve aralarındaki
 * besleyici wire'lar, kullanıcının normalde manuel çizdiği akışın aynısı —
 * BoxCommand, MainPanelCommand, PanelCommand tetiklenir → hem sahneye gerçek
 * panel mesh'leri eklenir, hem `simpleSupply` Redux state'ine component/feeder
 * yazılır. Üretilen wire'lar `componentId` taşır → panel çıkışı olarak
 * davranır, üzerlerine yeni hatlar eklenebilir.
 */
export function createDemoElectricalScene(scene, dispatch) {
  if (!scene || !dispatch) return;

  // ── 1. Supply (besleme kutusu) — sol ────────────────────────────────────
  const supplyId = generateUniqueId('supply');
  const supplyPos = { x: 0, y: 0 };
  const supplyComponent = ComponentFactory.createComponent('supply', {
    id: supplyId,
    name: 'Supply',
    position: { x: supplyPos.x, y: supplyPos.y, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
  });
  new BoxCommand(
    'ADD',
    supplyComponent,
    { position: [supplyPos.x, supplyPos.y, 0], rotation: 0 },
    { scene, dispatch }
  ).execute();

  // ── 2. ADP (Ana Dağıtım Panosu) + Supply→ADP main feeder ────────────────
  const mainPanelId = generateUniqueId('mainPanel');
  const mainWireId = generateUniqueId('wire');
  const mainFeederId = generateUniqueId('feeder');
  const mainPanelPos = { x: 120, y: 0 };
  const mainFeederVertices = [
    { x: supplyPos.x + 12.5, y: supplyPos.y, z: 0 }, // Supply sağ kenarı
    { x: mainPanelPos.x - 7.5, y: mainPanelPos.y, z: 0 }, // ADP sol kenarı
  ];
  const mainPanelComponent = ComponentFactory.createPanel('meter_panel', supplyId, {
    id: mainPanelId,
    name: 'ADP',
    position: { x: mainPanelPos.x, y: mainPanelPos.y, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    electrical: { phaseCount: 3, voltage: 380, purposeOfUse: 'Mesken' },
    metadata: { fromComponentId: supplyId, threejsGroupId: mainPanelId },
  });
  const mainFeeder = FeederFactory.createMainFeeder(supplyId, mainPanelId, {
    id: mainFeederId,
    vertices: mainFeederVertices,
    cable: { type: 'NYY', length: 12, route: mainFeederVertices },
    electrical: { voltage: 380, phaseCount: 3, purposeOfUse: 'Mesken' },
    metadata: { wireId: mainWireId },
  });
  new MainPanelCommand('ADD', mainPanelComponent, mainFeeder, {
    scene,
    dispatch,
    wireId: mainWireId,
    color: 0xff0000,
    sourceComponentId: supplyId,
    restoreVertices: mainFeederVertices,
    restoreSource: {
      componentId: supplyId,
      position: { x: supplyPos.x, y: supplyPos.y, z: 0 },
      name: 'Supply',
    },
    restoreCallback: () => {},
  }).execute();

  // ── 3. ZKT (Zayıf Akım Tablosu / Tali Pano) + ADP→ZKT feeder ────────────
  const zktId = generateUniqueId('panel');
  const zktWireId = generateUniqueId('wire');
  const zktFeederId = generateUniqueId('feeder');
  const zktPos = { x: 240, y: 0 };
  const zktFeederVertices = [
    { x: mainPanelPos.x + 7.5, y: mainPanelPos.y, z: 0 }, // ADP sağ kenarı
    { x: zktPos.x - 7.5, y: zktPos.y, z: 0 }, // ZKT sol kenarı
  ];
  const zktComponent = ComponentFactory.createPanel('dist_panel', mainPanelId, {
    id: zktId,
    name: 'ZKT',
    position: { x: zktPos.x, y: zktPos.y, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    electrical: { purposeOfUse: 'Mesken', aboneTuru: 'Mesken' },
    metadata: { fromPanelId: mainPanelId },
  });
  const zktFeeder = FeederFactory.createFeederForLoad(
    'panel', 0, mainPanelId, zktId,
    {
      id: zktFeederId,
      vertices: zktFeederVertices,
      cable: { type: 'NYM', crossSection: 4, length: 12 },
      electrical: { voltage: 220, current: 0 },
      metadata: { wireId: zktWireId },
    }
  );
  new PanelCommand('ADD', zktComponent, zktFeeder, {
    scene,
    dispatch,
    wireId: zktWireId,
    color: 0xff0000,
    sourcePanelId: mainPanelId,
    restoreVertices: zktFeederVertices,
    restoreSource: {
      componentId: mainPanelId,
      panelID: mainPanelId,
      position: { x: mainPanelPos.x, y: mainPanelPos.y, z: 0 },
      name: 'ADP',
    },
    restoreCallback: () => {},
  }).execute();

  // ── 4. ZKT'den 5 paralel priz hattı ─────────────────────────────────────
  // Güzergâh: sağ → yukarı → sağ → aşağı → sağ → yukarı (sonunda priz).
  // Her wire perpendicular-offset ile gerçek paralel ofset alır;
  // yatay segmentlerde y-shift, dikey segmentlerde x-shift. Böylece köşelerde
  // de wire'lar üst üste binmeden paralel kalır.
  const SOCKET_POWER = 3520; // W (standart priz)
  const ZKT_RIGHT_X = zktPos.x + 7.5; // ZKT sağ kenarı
  const wireColor = 0xff0000;

  for (let i = 0; i < 5; i++) {
    const off = (i - 2) * 6; // -12, -6, 0, 6, 12 (perpendicular ofset)
    const verts = [
      { x: ZKT_RIGHT_X,  y: 0 + off,    z: 0 },
      { x: 400 - off,    y: 0 + off,    z: 0 }, // R→U köşesi
      { x: 400 - off,    y: 200 + off,  z: 0 }, // U→R köşesi
      { x: 650 + off,    y: 200 + off,  z: 0 }, // R→D köşesi
      { x: 650 + off,    y: 50 + off,   z: 0 }, // D→R köşesi
      { x: 850 - off,    y: 50 + off,   z: 0 }, // R→U köşesi (son)
      { x: 850 - off,    y: 250 + off,  z: 0 }, // priz konumu
    ];
    const socketEnd = verts[verts.length - 1];

    const socketId  = generateUniqueId('socket');
    const socketWid = generateUniqueId('wire');
    const socketFid = generateUniqueId('feeder');

    const socketComponent = ComponentFactory.createSocket(SOCKET_POWER, {
      id: socketId,
      name: `Priz ${i + 1}`,
      position: { x: socketEnd.x, y: socketEnd.y, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      parentComponentId: zktId,
    });

    const cableLen = verts.reduce((sum, v, j, arr) => {
      if (j === 0) return 0;
      return sum + Math.hypot(v.x - arr[j - 1].x, v.y - arr[j - 1].y);
    }, 0);

    const socketFeeder = FeederFactory.createFeederForLoad(
      'socket', SOCKET_POWER, zktId, socketId,
      {
        id: socketFid,
        vertices: verts,
        cable: { type: 'NYM', crossSection: 2.5, length: Math.round(cableLen / 10) },
        electrical: { voltage: 220, current: SOCKET_POWER / 220 },
        metadata: { wireId: socketWid, color: wireColor },
      }
    );

    new SocketCommand('ADD', socketComponent, socketFeeder, {
      socketPosition: [socketEnd.x, socketEnd.y, 0],
      socketType: 'kapaklı',
      dir: 1,
      color: wireColor,
      wireVertices: verts,
      stretchLength: 0,
      rotateValue: 0,
    }).execute(scene, dispatch, false);
  }
}
